'use client';

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { IconXClose } from 'icon/General/x-close';
import { FOCUS_RING } from '../styles/common';
import { cn } from '../utils';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

export const DIALOG_OVERLAY_STYLES = `fixed inset-0 z-50 bg-linear-to-b from-black/40 to-black/70 backdrop-blur-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 dark:from-gray-950/50 dark:to-gray-950`;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    className={cn(DIALOG_OVERLAY_STYLES, className)}
    ref={ref}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

export const DIALOG_CONTENT_STYLES = cn(
  // basic
  'fixed start-[50%] top-[50%] z-50 grid w-[98%] max-w-xs translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border bg-primary p-6 shadow-xl duration-200 sm:w-full sm:max-w-sm md:w-full',
  // dark mode
  'dark:border-gray-800 dark:bg-gray-950',
  // animations
  'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
);

export const DIALOG_CLOSE_STYLES = cn(
  'absolute end-2 top-2 rounded-sm p-2 text-gray-500 transition data-[state=open]:bg-gray-100 data-[state=open]:text-gray-500 hover:bg-gray-50 disabled:pointer-events-none dark:data-[state=open]:bg-gray-800 dark:data-[state=open]:text-gray-400',
  FOCUS_RING,
);

export interface DialogContentProps extends React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> {
  /**
   * default true
   */
  showCloseButton?: boolean;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, showCloseButton = true, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      className={cn(DIALOG_CONTENT_STYLES, className)}
      ref={ref}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close className={DIALOG_CLOSE_STYLES}>
          <IconXClose height={24} width={24} />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

export const DIALOG_HEADER_STYLES = `flex flex-col space-y-1.5 text-start`;
function DialogHeader({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn(DIALOG_HEADER_STYLES, className)} {...props} />;
}
DialogHeader.displayName = 'DialogHeader';

export const DIALOG_FOOTER_STYLES = `flex flex-col-reverse gap-3 sm:flex-row sm:justify-end pt-4`;

function DialogFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn(DIALOG_FOOTER_STYLES, className)} {...props} />;
}
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    className={cn('text-lg font-semibold leading-none text-primary', className)}
    ref={ref}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    className={cn('text-sm text-tertiary', className)}
    ref={ref}
    asChild
    {...props}
  >
    <div>{props.children}</div>
  </DialogPrimitive.Description>
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

const DialogClose = DialogPrimitive.Close;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose,
};
